// AdminEmployeeFeedbackForm.jsx

import { useEffect, useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { UPDATE_ORDER_CALLING_STATUS } from "./graphql";

const CALL_STATUS_OPTIONS = [
  { label: "Pending", value: "PENDING" },
  { label: "Completed", value: "COMPLETED" },
  { label: "No Answer", value: "NO_ANSWER" },
  { label: "Not Reachable", value: "NOT_REACHABLE" },
];

const ISSUE_STATUS_OPTIONS = [
  { label: "No Issue", value: "NO_ISSUE" },
  { label: "Needs Resolution", value: "NEEDS_RESOLUTION" },
  { label: "Resolved", value: "RESOLVED" },
];

const GET_FEEDBACK_QUESTIONS = gql`
  query {
    getOrderFeedbackQuestions(targetType: "ADM-EMP") {
      status
      data {
        id
        question
        answerType
        options
      }
    }
  }
`;

const GET_ORDER_FEEDBACK = gql`
  query getOrderFeedbackByOrder($orderIds: [ID!]!) {
    getOrderFeedbackByOrder(orderIds: $orderIds) {
      status
      message
      data {
        id
        orderId
        adminToEmployeeRating
        adminToEmployeeComment
        userToEmployeeRating
        userToEmployeeComment
        userToProductRating
        answers {
          id
          selectedOptions
          question {
            id
            question
            answerType
          }
        }
        order {
          bookingId
          bookingDate
          timeSlot
          address
        }
      }
    }
  }
`;

const SUBMIT_ORDER_FEEDBACK = gql`
  mutation submitOrderFeedback($data: SubmitOrderFeedbackInput!) {
    submitOrderFeedback(data: $data) {
      status
      message
    }
  }
`;

const SmallLabel = ({ children }) => (
  <label className="text-white text-sm block mb-1">{children}</label>
);

const SectionCard = ({ title, children }) => (
  <div className="bg-[#1a1a1a] text-white p-4 rounded-md">
    {title && (
      <h3 className="text-[#FBB034] text-lg font-bold mb-3">{title}</h3>
    )}
    {children}
  </div>
);

const AdminEmployeeFeedbackForm = ({ order, onClose }) => {
  const { data, loading } = useQuery(GET_FEEDBACK_QUESTIONS);

  const { data: feedbackData, loading: feedbackLoading } = useQuery(
    GET_ORDER_FEEDBACK,
    {
      variables: { orderIds: [order.id] },
      fetchPolicy: "network-only",
    },
  );
  const [submitFeedback, { loading: submitting }] = useMutation(
    SUBMIT_ORDER_FEEDBACK,
  );

  const [employeeRating, setEmployeeRating] = useState(5);
  const [employeeComment, setEmployeeComment] = useState("");
  const [answers, setAnswers] = useState({});

  const [supportData, setSupportData] = useState({
    // The real call outcome. It used to show "Completed" whenever the customer
    // had rated in the app, which hid unhappy customers who still needed a call.
    feedbackCallStatus: order.feedbackCallStatus || "PENDING",
    feedbackCallComment: order.feedbackCallComment || "",
    feedbackIssueStatus: order.feedbackIssueStatus || "NO_ISSUE",
    feedbackIssueDetails: order.feedbackIssueDetails || "",
    feedbackResolutionDetails: order.feedbackResolutionDetails || "",
    cancellationCallStatus: order.cancellationCallStatus || "PENDING",
    cancellationCallComment: order.cancellationCallComment || "",
  });

  const [updateCallingStatus, { loading: savingSupport }] = useMutation(UPDATE_ORDER_CALLING_STATUS);

  const isCancelledOrDeclined = order.status === "Cancelled" || order.status === "Declined";

  const handleSupportChange = (field, value) => {
    setSupportData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveSupport = async () => {
    try {
      const res = await updateCallingStatus({
        variables: {
          data: {
            orderId: order.id,
            ...supportData,
          },
        },
      });

      if (res.data.updateOrderCallingStatus.status === "SUCCESS") {
        alert("Support details updated successfully!");
      } else {
        alert(res.data.updateOrderCallingStatus.message);
      }
    } catch (err) {
      console.error(err);
      alert("Error saving support details");
    }
  };

  const questions = data?.getOrderFeedbackQuestions?.data || [];
  const existingFeedback =
    feedbackData?.getOrderFeedbackByOrder?.data?.[0] ?? null;

  // What the customer said in the app decides whether a call is needed:
  // none is, unless they gave 1-2 stars for the service or the products.
  const appServiceRating = existingFeedback?.userToEmployeeRating || null;
  const appProductRating = existingFeedback?.userToProductRating || null;
  const ratedInApp = Boolean(appServiceRating || appProductRating);
  const unhappyInApp = (appServiceRating && appServiceRating <= 2) || (appProductRating && appProductRating <= 2);
  const callHint = !ratedInApp
    ? null
    : unhappyInApp
      ? { cls: "bg-red-900/40 border-red-500 text-red-200", text: `Customer rated ${[appServiceRating && `service ${appServiceRating}★`, appProductRating && `products ${appProductRating}★`].filter(Boolean).join(", ")} in the app. Call them back.` }
      : { cls: "bg-emerald-900/30 border-emerald-600 text-emerald-200", text: `Customer already rated ${appServiceRating ? `${appServiceRating}★` : "the booking"} in the app. A call isn't needed.` };
  const lastCall = order.feedbackCallAt
    ? `Last call logged ${new Date(order.feedbackCallAt).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })} by ${order.feedbackCallBy || "Admin"}${order.feedbackCallAttempts > 1 ? ` · ${order.feedbackCallAttempts} tries` : ""}`
    : null;

  /* ---------------------
     Handle Answer Change
  ---------------------- */
  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };
  const handleMultiSelect = (questionId, option) => {
    setAnswers((prev) => {
      const current = prev[questionId] || [];

      if (current.includes(option)) {
        return {
          ...prev,
          [questionId]: current.filter((o) => o !== option),
        };
      } else {
        return {
          ...prev,
          [questionId]: [...current, option],
        };
      }
    });
  };

  useEffect(() => {
    if (existingFeedback) {
      setEmployeeRating(existingFeedback.adminToEmployeeRating || 5);
      setEmployeeComment(existingFeedback.adminToEmployeeComment || "");

      const mappedAnswers = {};
      (existingFeedback.answers || []).forEach((a) => {
        if (a.question?.id) {
          const answerType = a.question.answerType;
          if (answerType === "MULTISELECT") {
            mappedAnswers[a.question.id] = a.selectedOptions || [];
          } else {
            mappedAnswers[a.question.id] = a.selectedOptions?.[0] ?? undefined;
          }
        }
      });

      setAnswers(mappedAnswers);
    }
  }, [existingFeedback]);

  /* ---------------------
     Submit
  ---------------------- */
  const handleSubmit = async () => {
    const formattedAnswers = questions
      .map((q) => {
        const value = answers[q.id];

        if (!value) return null;

        if (q.answerType === "RATING")
          return { questionId: q.id, ratingValue: Number(value) };

        if (q.answerType === "BOOLEAN")
          return { questionId: q.id, booleanValue: value === "true" };

        if (q.answerType === "TEXT")
          return { questionId: q.id, textValue: value };

        if (q.answerType === "SELECT")
          return { questionId: q.id, selectedOption: value };

        if (q.answerType === "MULTISELECT") {
          if (!value || value.length === 0) return null;
          return {
            questionId: q.id,
            selectedOptions: Array.isArray(value) ? value : [value],
          };
        }

        return null;
      })
      .filter(Boolean);

    try {
      const res = await submitFeedback({
        variables: {
          data: {
            orderId: order.id,
            adminToEmployeeRating: employeeRating,
            adminToEmployeeComment: employeeComment,
            answers: formattedAnswers,
          },
        },
      });

      if (res.data.submitOrderFeedback.status === "SUCCESS") {
        alert(existingFeedback ? "Feedback updated!" : "Feedback submitted!");
        if (onClose) onClose();
      } else {
        alert("Failed");
      }
    } catch (err) {
      console.error(err);
      alert("Error submitting feedback");
    }
  };

  if (loading)
    return <p className="text-gray-400">Loading feedback questions...</p>;

  return (
    <div className="flex flex-col space-y-6">
      {/* Booking Header */}
      <SectionCard title="Booking Info">
        <p>
          <strong>Booking ID:</strong> {order.bookingId}
        </p>
        <p>
          <strong>Employee:</strong> {order.employee?.fullName}
        </p>
      </SectionCard>

      {/* Overall Rating */}
      <SectionCard title="Overall Employee Rating">
        <SmallLabel>Rating (1-5)</SmallLabel>
        <select
          value={employeeRating}
          onChange={(e) => setEmployeeRating(Number(e.target.value))}
          className="bg-[#0c0b0b] text-white px-3 py-2 border border-gray-600 rounded"
        >
          {[1, 2, 3, 4, 5].map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>

        <SmallLabel className="mt-4">Comment</SmallLabel>
        <textarea
          value={employeeComment}
          onChange={(e) => setEmployeeComment(e.target.value)}
          className="bg-[#0c0b0b] text-white px-3 py-2 border border-gray-600 rounded w-full h-20"
          placeholder="Optional comment..."
        />
      </SectionCard>

      {/* Dynamic Questions */}
      <SectionCard title="Detailed Feedback">
        {questions.map((q) => (
          <div key={q.id} className="mb-4">
            <SmallLabel>{q.question}</SmallLabel>

            {q.answerType === "TEXT" && (
              <textarea
                value={answers[q.id] || ""}
                onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                className="bg-[#0c0b0b] text-white px-3 py-2 border border-gray-600 rounded w-full h-16"
              />
            )}

            {q.answerType === "BOOLEAN" && (
              <select
                value={answers[q.id] !== undefined ? String(answers[q.id]) : ""}
                onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                className="bg-[#0c0b0b] text-white px-3 py-2 border border-gray-600 rounded"
              >
                <option value="">Select</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            )}

            {q.answerType === "RATING" && (
              <select
                value={answers[q.id] || ""}
                onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                className="bg-[#0c0b0b] text-white px-3 py-2 border border-gray-600 rounded"
              >
                {[1, 2, 3, 4, 5].map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            )}

            {q.answerType === "SELECT" && (
              <select
                value={answers[q.id] || ""}
                onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                className="bg-[#0c0b0b] text-white px-3 py-2 border border-gray-600 rounded"
              >
                <option value="">Select</option>
                {q.options?.map((opt, i) => (
                  <option key={i} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            )}
            {q.answerType === "MULTISELECT" && (
              <div className="flex flex-wrap gap-2 mt-2">
                {q.options?.map((opt, i) => {
                  const selected = (answers[q.id] || []).includes(opt);

                  return (
                    <div
                      key={i}
                      onClick={() => handleMultiSelect(q.id, opt)}
                      className={`px-3 py-1 rounded-full cursor-pointer text-sm border transition-all duration-200
      ${
        selected
          ? "bg-[#FBB034] text-black border-[#FBB034]"
          : "bg-transparent text-white border-gray-500 hover:border-white"
      }
  `}
                    >
                      {opt}
                      {selected && <span className="ml-1">✓</span>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
        {/* Submit */}
        <div className="flex justify-end mt-4">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-[#FBB034] text-black px-6 py-2 rounded-md font-bold text-sm"
          >
            {submitting ? "Submitting..." : "Save Employee Rating"}
          </button>
        </div>
      </SectionCard>

      {/* Post-Service Feedback */}
      {!isCancelledOrDeclined && (
        <SectionCard title="Customer Post-Service Feedback">
          {callHint && (
            <div className={`mb-4 border rounded px-3 py-2 text-sm ${callHint.cls}`}>{callHint.text}</div>
          )}
          {lastCall && <p className="mb-3 text-xs text-gray-400">{lastCall}</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <SmallLabel>Call Status</SmallLabel>
              <select
                value={supportData.feedbackCallStatus}
                onChange={(e) => handleSupportChange("feedbackCallStatus", e.target.value)}
                className="w-full bg-[#0c0b0b] text-white px-3 py-2 border border-gray-600 rounded outline-none focus:border-[#FBB034]"
              >
                {CALL_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <SmallLabel>Issue Status</SmallLabel>
              <select
                value={supportData.feedbackIssueStatus}
                onChange={(e) => handleSupportChange("feedbackIssueStatus", e.target.value)}
                className="w-full bg-[#0c0b0b] text-white px-3 py-2 border border-gray-600 rounded outline-none focus:border-[#FBB034]"
              >
                {ISSUE_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <SmallLabel>Call Comments</SmallLabel>
              <textarea
                value={supportData.feedbackCallComment}
                onChange={(e) => handleSupportChange("feedbackCallComment", e.target.value)}
                className="w-full bg-[#0c0b0b] text-white px-3 py-2 border border-gray-600 rounded outline-none focus:border-[#FBB034] min-h-[60px]"
                placeholder="Notes from the feedback call..."
              />
            </div>

            {supportData.feedbackIssueStatus !== "NO_ISSUE" && (
              <>
                <div className="md:col-span-2">
                  <SmallLabel>Issue Details</SmallLabel>
                  <textarea
                    value={supportData.feedbackIssueDetails}
                    onChange={(e) => handleSupportChange("feedbackIssueDetails", e.target.value)}
                    className="w-full bg-[#0c0b0b] text-white px-3 py-2 border border-gray-600 rounded outline-none focus:border-[#FBB034] min-h-[60px]"
                    placeholder="Describe the issue reported by the customer..."
                  />
                </div>
                {supportData.feedbackIssueStatus === "RESOLVED" && (
                  <div className="md:col-span-2">
                    <SmallLabel>Resolution Details</SmallLabel>
                    <textarea
                      value={supportData.feedbackResolutionDetails}
                      onChange={(e) => handleSupportChange("feedbackResolutionDetails", e.target.value)}
                      className="w-full bg-[#0c0b0b] text-white px-3 py-2 border border-gray-600 rounded outline-none focus:border-[#FBB034] min-h-[60px]"
                      placeholder="How was this issue resolved?..."
                    />
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex justify-end mt-4">
            <button
              onClick={handleSaveSupport}
              disabled={savingSupport}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md font-bold text-sm"
            >
              {savingSupport ? "Saving..." : "Save Customer Support"}
            </button>
          </div>
        </SectionCard>
      )}

      {/* Cancellation Calls Section */}
      {isCancelledOrDeclined && (
        <SectionCard title="Customer Cancellation & Decline Call">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <SmallLabel>Call Status</SmallLabel>
              <select
                value={supportData.cancellationCallStatus}
                onChange={(e) => handleSupportChange("cancellationCallStatus", e.target.value)}
                className="w-full bg-[#0c0b0b] text-white px-3 py-2 border border-gray-600 rounded outline-none focus:border-[#FBB034]"
              >
                {CALL_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <SmallLabel>Call Comments</SmallLabel>
              <textarea
                value={supportData.cancellationCallComment}
                onChange={(e) => handleSupportChange("cancellationCallComment", e.target.value)}
                className="w-full bg-[#0c0b0b] text-white px-3 py-2 border border-gray-600 rounded outline-none focus:border-[#FBB034] min-h-[60px]"
                placeholder="Notes from the cancellation call..."
              />
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <button
              onClick={handleSaveSupport}
              disabled={savingSupport}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md font-bold text-sm"
            >
              {savingSupport ? "Saving..." : "Save Customer Support"}
            </button>
          </div>
        </SectionCard>
      )}

    </div>
  );
};

export default AdminEmployeeFeedbackForm;
